import { useLicence } from '../contexts/LicenceContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Lock } from 'lucide-react';

/**
 * Two UI pieces in one:
 *   • Full-screen lockout when trial has expired or subscription is not active
 *   • Slim top banner when trial is ≤ 7 days from ending
 * Both deep-link to /settings/subscription.
 */
export default function TrialBanner() {
  const licence = useLicence();
  const nav = useNavigate();
  const loc = useLocation();
  if (!licence) return null;

  const search = new URLSearchParams(loc.search);
  const onSubsPage = loc.pathname.startsWith('/settings') && search.get('section') === 'subscription';
  const days = licence.trial_remaining_days;
  const expired = licence.trial_expired;
  const status = licence.licence_status;
  const needsPlan = expired || ['cancelled', 'expired', 'past_due'].includes(status);

  if (needsPlan && !onSubsPage) {
    // Full-screen blocker
    return (
      <div
        className="fixed inset-0 z-[100] bg-[#1A2421]/70 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="trial-expired-lockout"
      >
        <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl">
          <div className="w-14 h-14 bg-[#F5F0E6] rounded-full flex items-center justify-center mb-5 mx-auto">
            <Lock size={28} className="text-[#D4A373]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1A2421] mb-2 text-center" style={{ fontFamily: 'Outfit' }}>
            {expired ? 'Your free trial has ended' : 'Your subscription is not active'}
          </h2>
          <p className="text-[#5C6656] text-sm mb-6 text-center leading-relaxed">
            Your data is safe — your calendar, clients, and history are all right here.
            To continue booking appointments, processing payments, and editing your business, choose a plan below.
          </p>
          <button
            onClick={() => nav('/settings?section=subscription')}
            className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white font-semibold py-3 rounded-lg transition-colors"
            data-testid="trial-expired-choose-plan-btn"
          >
            Choose your plan
          </button>
          <p className="text-center text-xs text-[#8A9075] mt-4">
            Questions? Email us at hello@apptio.app
          </p>
        </div>
      </div>
    );
  }

  // Warning banner when ≤ 7 days left on trial (and not expired)
  if (status === 'trial' && !expired && days <= 7) {
    const urgent = days <= 2;
    return (
      <div
        className={`${urgent ? 'bg-[#E2725B]' : 'bg-[#D4A373]'} text-white px-4 py-2 text-sm flex items-center justify-between gap-3`}
        data-testid="trial-ending-banner"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} />
          <span className="font-medium">
            {days === 0
              ? 'Your trial ends today'
              : days === 1
              ? 'Your trial ends tomorrow'
              : `${days} days left on your free trial`}
          </span>
        </div>
        <button
          onClick={() => nav('/settings?section=subscription')}
          className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded font-medium text-xs"
          data-testid="trial-banner-choose-plan-btn"
        >
          Choose a plan →
        </button>
      </div>
    );
  }

  return null;
}
